import React, { useEffect, useMemo, useState, forwardRef,
  useImperativeHandle } from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import  FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

type Props = {
  onValidationChange?: (isValid: boolean) => void;
};

export interface ReceiveAmountStepHandle {
  submit: () => Promise<boolean>;
}

const ReceiveAmountStep = forwardRef<ReceiveAmountStepHandle, Props>(({ onValidationChange }: Props, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
          amount: "",
  });
  const receiverReceiveAmount = useSessionStore((s: SessionState) => s.receiverReceiveAmount);
  const referenceNumberSearchResult = useSessionStore((s: SessionState) => (s as any).referenceNumberSearchResult);
  const setReceiverReceiveAmount = useSessionStore((s: SessionState) => (s as any).setReceiverReceiveAmount);
  const validationError = useSessionStore((s: SessionState) => (s as any).receiveAmountValidationError);
  const setValidationError = useSessionStore((s: SessionState) => (s as any).setReceiveAmountValidationError);
  const [amount, setAmount] = useState<string>(
    () => String((useSessionStore.getState() as any).receiverReceiveAmount ?? "")
  );

  useEffect(() => {
    setAmount(String(receiverReceiveAmount ?? ""));
  }, [receiverReceiveAmount]);

  const parseAmount = (text: string): string => {
    const cleaned = text.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      return `${parts[0]}.${parts.slice(1).join("")}`;
    }
    return cleaned;
  };

  const isValid = useMemo(() => {
    const n = parseFloat(amount || "0");
    return !!amount && isFinite(n) && n > 0;
  }, [amount]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  // expose validate function
    useImperativeHandle(ref, () => ({
      submit: async () => {
        const newErrors = {
          amount: amount.trim() ? "" : "Invalid amount",
        };
        setErrors(newErrors);
      
        // if any error string is non-empty
        const isValid = !Object.values(newErrors).some(Boolean);
        return isValid;
      },
    }));

  const handleChange = (text: string) => {
    const next = parseAmount(text);
    setAmount(next);
    try { setReceiverReceiveAmount?.(next); } catch {}
    try { setValidationError?.(null); } catch {}
    if (errors.amount) setErrors({ ...errors, amount: "" });
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("HOW_MUCH_ARE_YOU_RECEIVING")}</Text>
      <Text style={styles.subtitle}>{t("PLEASE_ENTER_AMOUNT")}</Text>
      <FloatingTextInputComponent
        testID="receiveAmount"
        label={t("RECEIVE_AMOUNT")}
        value={amount}
        isAutoFocus={true}
        onChangeText={handleChange}
        keyboardType={"decimal-pad"}
        errorText={errors.amount}
        onClearError={() => setErrors((prev) => ({ ...prev, amount: "" }))}
      />
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 20
  },
 
});

export default ReceiveAmountStep;


